<div class="container-margin">
  <h1 class="header">{{ title }}</h1>
  <p>{{ translationService.getCsvTemplateMappingHelpText() }}</p>
  @if (!isEditMode) {
    <div class="preset-actions">
      <button
        mat-stroked-button
        type="button"
        [class.selected-preset]="selectedPreset === 'marketplace'"
        [attr.aria-pressed]="selectedPreset === 'marketplace'"
        [matTooltip]="translationService.getCsvMarketplaceDefaultsTooltipText()"
        (click)="useDefaults(true)"
      >
        <mat-icon>{{
          selectedPreset === 'marketplace' ? 'check' : 'info_outline'
        }}</mat-icon>
        {{ translationService.getCsvMarketplaceDefaultsText() }}
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.selected-preset]="selectedPreset === 'chemicalRecycling'"
        [attr.aria-pressed]="selectedPreset === 'chemicalRecycling'"
        [matTooltip]="translationService.getCsvFullDefaultsTooltipText()"
        (click)="useDefaults(false)"
      >
        <mat-icon>{{
          selectedPreset === 'chemicalRecycling' ? 'check' : 'info_outline'
        }}</mat-icon>
        {{ translationService.getCsvFullDefaultsText() }}
      </button>
    </div>
  }
  <form [formGroup]="templateForm" (ngSubmit)="onSubmit()">
    <mat-form-field class="template-name" appearance="outline">
      <mat-label>{{ translationService.getCsvTemplateNameText() }}</mat-label>
      <input matInput formControlName="name" autocomplete="off" />
      <mat-error>{{ fieldError('name') }}</mat-error>
    </mat-form-field>
    <h2>{{ translationService.getCsvCoreMappingsText() }}</h2>
    <div class="mapping-grid">
      @for (field of requiredFields; track field.name) {
        <mat-form-field appearance="outline">
          <mat-label>{{ field.label }}</mat-label>
          <input matInput [formControlName]="field.name" autocomplete="off" />
          <mat-error>{{ fieldError(field.name) }}</mat-error>
        </mat-form-field>
      }
    </div>
    <h2>{{ translationService.getCsvOptionalMappingsText() }}</h2>
    <div class="mapping-grid">
      @for (field of optionalFields; track field.name) {
        <mat-form-field appearance="outline">
          <mat-label>{{ field.label }}</mat-label>
          <input matInput [formControlName]="field.name" autocomplete="off" />
          <mat-error>{{ fieldError(field.name) }}</mat-error>
        </mat-form-field>
      }
    </div>
    <h2>{{ translationService.getCsvDroneMappingsText() }}</h2>
    <div class="mapping-grid">
      @for (field of droneFields; track field.name) {
        <mat-form-field appearance="outline">
          <mat-label>{{ field.label }}</mat-label>
          <input matInput [formControlName]="field.name" autocomplete="off" />
          <mat-error>{{ fieldError(field.name) }}</mat-error>
        </mat-form-field>
      }
    </div>
    <h2>{{ translationService.getCustomFieldsText() }}</h2>
    <p>{{ translationService.getCsvCustomFieldsHelpText() }}</p>
    <div formArrayName="customFields" class="custom-field-list">
      @for (field of customFields.controls; track field; let i = $index) {
        <div [formGroupName]="i" class="custom-field-row">
          <mat-form-field appearance="outline">
            <mat-label>{{
              translationService.getCustomFieldNameText()
            }}</mat-label>
            <input
              matInput
              formControlName="name"
              autocomplete="off"
              (blur)="syncCustomFieldColumn(i)"
            />
            <mat-error>{{
              fieldError('customFields.' + i + '.name')
            }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{
              translationService.getCsvColumnHeadingText()
            }}</mat-label>
            <input matInput formControlName="column" autocomplete="off" />
            <mat-error>{{
              fieldError('customFields.' + i + '.column')
            }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{
              translationService.getCustomFieldTypeText()
            }}</mat-label>
            <mat-select
              formControlName="type"
              (selectionChange)="onCustomFieldTypeChange(i)"
            >
              <mat-option value="text">{{
                translationService.getCustomFieldTextText()
              }}</mat-option>
              <mat-option value="number">{{
                translationService.getCustomFieldNumberText()
              }}</mat-option>
              <mat-option value="boolean">{{
                translationService.getCustomFieldBooleanText()
              }}</mat-option>
            </mat-select>
            <mat-error>{{
              fieldError('customFields.' + i + '.type')
            }}</mat-error>
          </mat-form-field>
          @if (field.controls.type.value === 'number') {
            <mat-form-field appearance="outline">
              <mat-label>{{
                translationService.getCustomFieldUnitText()
              }}</mat-label>
              <input matInput formControlName="unit" autocomplete="off" />
              <mat-error>{{
                fieldError('customFields.' + i + '.unit')
              }}</mat-error>
            </mat-form-field>
          }
          <button
            mat-icon-button
            type="button"
            [attr.aria-label]="
              translationService.getRemoveCustomFieldText() +
              ': ' +
              field.controls.name.value
            "
            (click)="removeCustomField(i)"
          >
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      }
    </div>
    @if (customFields.hasError('mapping')) {
      <p class="custom-fields-error" role="alert">
        {{ fieldError('customFields') }}
      </p>
    }
    <button
      mat-stroked-button
      type="button"
      (click)="addCustomField()"
      [disabled]="customFields.length >= 50"
    >
      <mat-icon>add</mat-icon>{{ translationService.getAddCustomFieldText() }}
    </button>
    <details #additionalMappings class="additional-mappings">
      <summary>{{ translationService.getCsvAdditionalMappingsText() }}</summary>
      <div class="mapping-grid">
        @for (field of additionalFields; track field.name) {
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>{{ field.label }}</mat-label>
            <input
              matInput
              [formControlName]="field.name"
              [attr.data-mapping-field]="field.name"
              autocomplete="off"
            />
            <mat-error>{{ fieldError(field.name) }}</mat-error>
          </mat-form-field>
        }
      </div>
    </details>
    <div class="actions">
      <button mat-raised-button type="submit" color="primary">
        <mat-icon>save</mat-icon>{{ translationService.getSubmitText() }}
      </button>
      <button mat-stroked-button type="button" (click)="onReset()">
        <mat-icon>refresh</mat-icon>{{ translationService.getResetText() }}
      </button>
    </div>
  </form>
</div>
